import { z } from 'zod';
import { CompetenciaChave, CompetenciaChaveQuery, DataISO, Id } from './comum.js';
import { TipoGuiaEntrega } from './entregaveis.js';
import { LeituraGuia } from './detalhe.js';

/**
 * Caixa de entrada de guias (docs/guias-e-entregas.md §3.4, fatia 3a): PDFs soltos fora de uma tarefa. O sistema lê CNPJ,
 * competência, tipo e código, acha a tarefa e registra a guia nela; o que não bate fica "para resolver" — sempre com o
 * arquivo guardado — com a sugestão e um seletor de cliente/tarefa (ou "criar tarefa"). Na tela não existe "triagem".
 */
/** DIVIDIDA = o lote (um PDF com várias guias) que foi dividido num arquivo por guia: fica o registro, sem arquivo próprio; RELIDA = o pendente que foi solto de novo e lido outra vez */
export const CaixaSituacao = z.enum(['PENDENTE', 'RESOLVIDA', 'DESCARTADA', 'REPETIDA', 'DIVIDIDA', 'RELIDA']);
export type CaixaSituacao = z.infer<typeof CaixaSituacao>;

/**
 * uma tarefa do cliente em que a guia pode entrar; `compativel` = aberta, na competência e da obrigação que a leitura indica;
 * `prioridade` = posição da obrigação da tarefa na lista que a leitura sugeriu (0 = a mais provável); `guiaViva` = a guia
 * desse tipo/código que a tarefa já tem (reemissão? então "substitui a guia anterior")
 */
export const CaixaCandidata = z.object({
  tarefaId: Id, titulo: z.string(), obrigacaoSigla: z.string().nullable(), competenciaLabel: z.string().nullable(), status: z.string(), vencimento: DataISO.nullable(), compativel: z.boolean(),
  prioridade: z.number().int().nullable().optional(),
  guiaViva: z.object({ entregaId: Id, nome: z.string(), vencimento: DataISO.nullable(), valor: z.number().nullable() }).nullable().optional(),
});
export type CaixaCandidata = z.infer<typeof CaixaCandidata>;

export const CaixaItem = z.object({
  id: Id, situacao: CaixaSituacao, nomeArquivo: z.string(), anexoId: Id.nullable(), quando: z.string(), porApelido: z.string().nullable(),
  /** resolvida sozinha (tarefa única e aberta na competência) */ automatica: z.boolean(), motivo: z.string().nullable(),
  leitura: LeituraGuia.nullable(), pagarAte: DataISO.nullable(), cnpj: z.string().nullable(),
  clienteId: Id.nullable(), clienteNome: z.string().nullable(),
  /** pendente: as tarefas sugeridas (as compatíveis primeiro) */ candidatas: z.array(CaixaCandidata),
  /** resolvida: onde a guia entrou */ tarefaId: Id.nullable(), tarefaTitulo: z.string().nullable(), entregaId: Id.nullable(), guiaNome: z.string().nullable(), resolvidaEm: z.string().nullable(),
  /** repetida: onde o mesmo arquivo já está */ repetidoDe: z.object({ anexoId: Id, tarefaId: Id.nullable(), tarefaTitulo: z.string().nullable() }).nullable(),
});
export type CaixaItem = z.infer<typeof CaixaItem>;

export const CaixaReceberResposta = z.object({ itens: z.array(CaixaItem) });
export type CaixaReceberResposta = z.infer<typeof CaixaReceberResposta>;

export const CaixaQuery = z.object({ situacao: z.enum(['PENDENTE', 'TODAS']).default('PENDENTE'), dias: z.coerce.number().int().min(1).max(90).default(30) });
export type CaixaQuery = z.infer<typeof CaixaQuery>;
export const CaixaLista = z.object({ itens: z.array(CaixaItem), pendentes: z.number().int() });
export type CaixaLista = z.infer<typeof CaixaLista>;

export const CaixaCandidatasQuery = z.object({ clienteId: Id, competenciaChave: CompetenciaChaveQuery.optional(), /** códigos de obrigação separados por vírgula */ obrigacoes: z.string().max(200).optional(), /** para achar a guia que a tarefa já tem desse tipo/código */ tipoGuia: z.string().max(10).optional(), codigoReceita: z.string().max(20).optional() });
export type CaixaCandidatasQuery = z.infer<typeof CaixaCandidatasQuery>;

const Valor = z.number().min(0).max(999_999_999_999);
/** o que a pessoa corrigiu na leitura antes de registrar (o resto vem do PDF) */
export const CaixaCampos = z.object({
  tipoGuia: TipoGuiaEntrega.nullable().optional(), codigoReceita: z.string().trim().max(20).nullable().optional(), competenciaChave: CompetenciaChave.nullable().optional(),
  vencimento: DataISO.nullable().optional(), pagarAte: DataISO.nullable().optional(), valorTotal: Valor.nullable().optional(), titulo: z.string().trim().max(120).nullable().optional(),
  /** reemissão: a guia anterior da tarefa que esta substitui (sai do portal, dos lembretes e dos relatórios) */
  substituiEntregaId: Id.nullable().optional(),
});
export type CaixaCampos = z.infer<typeof CaixaCampos>;
export const CaixaResolverBody = z.object({
  /** a tarefa escolhida… */ tarefaId: Id.nullable().default(null),
  /** …ou uma tarefa nova para este cliente (título e vencimento padrão vêm da guia) */
  novaTarefa: z.object({ clienteId: Id, titulo: z.string().trim().min(2).max(200).nullable().default(null), vencimento: DataISO.nullable().default(null), departamentoSigla: z.string().trim().max(8).nullable().default(null) }).nullable().default(null),
  campos: CaixaCampos.default({}),
  publicar: z.boolean().default(true),
  /** o que for publicado entra no envio ao cliente (janela; dá para desfazer) */ enviar: z.boolean().default(false),
}).refine((b) => !!b.tarefaId !== !!b.novaTarefa, { message: 'escolha a tarefa ou peça uma tarefa nova' });
export type CaixaResolverBody = z.infer<typeof CaixaResolverBody>;
export const CaixaResolverResposta = z.object({
  item: CaixaItem, /** o que merece atenção (CNPJ diferente, não entrou no envio…) */ avisos: z.array(z.string()),
  envio: z.object({ remessaId: Id, enviarApos: z.string().nullable(), urgente: z.boolean().optional() }).nullable(),
  /** a tarefa nova que a Caixa criou para esta guia */ tarefaCriada: z.object({ id: Id, titulo: z.string() }).nullable().optional(),
});
export type CaixaResolverResposta = z.infer<typeof CaixaResolverResposta>;
export const CaixaDescartarBody = z.object({ motivo: z.string().trim().max(300).nullable().default(null) });
export type CaixaDescartarBody = z.infer<typeof CaixaDescartarBody>;
